import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/axios';

/**
 * Slack members deliberately excluded from attendance. Kept visible rather
 * than hidden: an ignore is a decision someone made, and it should be
 * reversible without a DB edit.
 */
const AttendanceIgnored = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/attendance/ignored');
      setRows(res.data.data || []);
    } catch (err) {
      setError('Could not load the untracked list.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const unignore = async (slackUserId) => {
    try {
      await api.delete(`/api/attendance/ignored/${slackUserId}`);
      toast.success('Member will be tracked again from the next run');
      load();
    } catch (err) {
      toast.error('Could not update the untracked list');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Link to="/attendance" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to Attendance
        </Link>
        <h1 className="mt-2 text-2xl font-semibold text-gray-900">Untracked Slack members</h1>
        <p className="mt-1 text-sm text-gray-500">
          These channel members are excluded from attendance on purpose. Stop ignoring one and
          the next ingest run will pick them up, provided they are linked to an employee.
        </p>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        {error && <p className="p-6 text-sm text-red-700">{error}</p>}
        {!error && loading && <p className="p-6 text-sm text-gray-500">Loading...</p>}
        {!error && !loading && rows.length === 0 && (
          <p className="p-10 text-center text-sm text-gray-500">No untracked members.</p>
        )}
        {!error && !loading && rows.length > 0 && (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-3 text-left font-medium text-gray-500">Slack email</th>
                <th scope="col" className="px-4 py-3 text-left font-medium text-gray-500">Slack id</th>
                <th scope="col" className="px-4 py-3 text-left font-medium text-gray-500">Reason</th>
                <th scope="col" className="px-4 py-3 text-left font-medium text-gray-500">Since</th>
                <th scope="col" className="px-4 py-3 text-right font-medium text-gray-500">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => (
                <tr key={r.slack_user_id}>
                  <td className="px-4 py-3 text-gray-900">{r.slack_email || '-'}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{r.slack_user_id}</td>
                  <td className="px-4 py-3 text-gray-600">{r.reason || '-'}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-500">
                    {new Date(r.ignored_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => unignore(r.slack_user_id)}
                      className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Stop ignoring
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AttendanceIgnored;
